"use client";
/**
 * Field Component System
 *
 * A comprehensive system for building form fields and input components with consistent
 * styling, accessibility, and behavior. This module provides both base components and
 * styling utilities that can be composed to create complex form controls.
 *
 * Key Features:
 * - Consistent styling across all form elements
 * - Built-in state handling (invalid, disabled, focused)
 * - Accessibility support out of the box
 * - Flexible composition through style utilities
 *
 * @example Basic Usage
 * ```tsx
 * <Label htmlFor="name">Name</Label>
 * <Input id="name" size="md" />
 * ```
 *
 * @example Complex Field with All Components
 * ```tsx
 * <div>
 *   <Label htmlFor="email" tooltip="Enter your work email">Email</Label>
 *   <Input
 *     id="email"
 *     type="email"
 *     isInvalid={hasError}
 *     isDisabled={isSubmitting}
 *   />
 *   <Description>We'll never share your email</Description>
 *   {hasError && <FieldError>Please enter a valid email</FieldError>}
 * </div>
 * ```
 */

import React from "react";
import type { TextProps } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { controlStyles, type Size } from "../../utils/controlStyles";
import { Icon } from "../Icon/Icon";
import { Tooltip } from "../Tooltip/Tooltip";

// Base style props interface for all input-like components
export interface InputStyleProps {
  /** Whether the input is in an invalid state */
  isInvalid?: boolean;
  /** Whether the input is disabled */
  isDisabled?: boolean;
  /** Whether the input should have a transparent background */
  transparent?: boolean;
  /** The size variant of the input */
  size?: Size;
  /** Whether the input is currently focused */
  isFocused?: boolean;
  /** Additional CSS classes to apply */
  className?: string;
}

// Common interfaces
export interface BaseProps {
  /** The size variant of the component */
  size?: Size;
  /** Additional CSS classes to apply */
  className?: string;
}

export type DescriptionPlacement = "below" | "tooltip" | "inline" | "hidden";

export interface BaseInputProps extends BaseProps {
  /** Whether the input should have a transparent background */
  transparent?: boolean;
  /** Whether to show a clear button when the input has a value */
  isClearable?: boolean;
  /** Callback when the clear button is clicked */
  onClear?: () => void;
  /** Whether to show a search icon */
  showSearchIcon?: boolean;
  /**
   * Whether to reserve space for error messages to prevent layout shift
   * @default true
   */
  reserveErrorSpace?: boolean;
  /**
   * Where to display the description text
   * - "below": Show below the field (default)
   * - "tooltip": Show as a tooltip on the label icon
   * - "inline": Show inline with the label
   * - "hidden": Don't display the description
   * @default "below"
   */
  descriptionPlacement?: DescriptionPlacement;
}

export interface LabelProps extends BaseProps {
  /** The label content */
  children: React.ReactNode;
  /** Optional tooltip text to show next to the label */
  tooltip?: string;
  /** Optional description to show as tooltip or inline */
  description?: string;
  /** Where to display the description (only used if description is provided) */
  descriptionPlacement?: DescriptionPlacement;
  /** Whether to show a required field indicator (*) */
  isRequired?: boolean;
  /** The ID of the input this label is associated with */
  htmlFor?: string;
}

export interface FieldErrorProps extends BaseProps {
  /** The error message to display */
  children: React.ReactNode;
}

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">, InputStyleProps {
  /** The size variant of the input */
  size?: Size;
}

export interface DescriptionProps extends Omit<TextProps, "className">, BaseProps {
  /** The description content */
  children: React.ReactNode;
}

export interface FieldGroupProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "children">, InputStyleProps {
  /** Whether any child of the group is focused */
  isFocusWithin?: boolean;
  /** Children can be either a ReactNode or a render function */
  children?: React.ReactNode | ((props: InputStyleProps) => React.ReactNode);
}

/**
 * Hook for managing input focus state
 */
export function useInputFocus() {
  const [isFocused, setIsFocused] = React.useState(false);

  const handleFocus = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>, onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void) => {
      setIsFocused(true);
      onFocus?.(e);
    },
    []
  );

  const handleBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>, onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void) => {
      setIsFocused(false);
      onBlur?.(e);
    },
    []
  );

  return { isFocused, handleFocus, handleBlur };
}

/**
 * Wrapper component for input containers
 */
export const InputWrapper = React.forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode;
    className?: string;
  }
>(({ children, className }, ref) => {
  return (
    <div ref={ref} className={twMerge("relative w-full", className)}>
      {children}
    </div>
  );
});

InputWrapper.displayName = "InputWrapper";

/**
 * Clear button component for inputs
 */
export function ClearButton({
  onClick,
  size = "md",
  className,
}: {
  onClick: () => void;
  size?: Size;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      className={twMerge(
        "absolute right-2 top-[48%] -translate-y-1/2 transform text-text-muted hover:text-text-body cursor-pointer",
        className
      )}
      aria-label="Clear input"
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <Icon name="X" size={size === "sm" ? 16 : 20} />
    </button>
  );
}

/**
 * Generates state-specific styles for inputs based on their current state
 * (invalid, disabled, focused).
 *
 * State Priority:
 * 1. Disabled (overrides all other states)
 * 2. Invalid + Focused
 * 3. Invalid
 * 4. Focused
 * 5. Default
 *
 * @example
 * ```tsx
 * const styles = getInputStateStyles({ isInvalid: true, isFocused: true });
 * ```
 */
export function getInputStateStyles(props: { isInvalid?: boolean; isDisabled?: boolean; isFocused?: boolean }) {
  const { isInvalid, isDisabled, isFocused } = props;
  const baseStyles = "border rounded-[var(--control-border-radius)] !outline-none ";

  // Disabled state overrides all others
  if (isDisabled) {
    return `${baseStyles} border-border-muted`;
  }

  // Handle combinations of invalid and focus states
  if (isInvalid && isFocused) {
    return `${baseStyles} border-feedback-error-border shadow-[inset_0_0_0_1px_var(--color-feedback-error-border)]`;
  }

  if (isInvalid) {
    return `${baseStyles} border-feedback-error-border`;
  }

  if (isFocused) {
    return `${baseStyles} border-border-focus shadow-[inset_0_0_0_1px_var(--color-border-focus)]`;
  }

  // Default state - explicitly set border color
  return `${baseStyles} border-border-input`;
}

/**
 * Generates background styles based on the transparent and disabled props.
 * Uses Tailwind classes for consistent styling across light/dark modes.
 */
export function getInputBackgroundStyles(props: { transparent?: boolean; isDisabled?: boolean }) {
  if (props.isDisabled) {
    return "bg-background-muted";
  }
  return props.transparent ? "bg-transparent" : "bg-background-input";
}

/**
 * Returns the mobile font-size override class for input components.
 * iOS Safari zooms in on inputs with font-size < 16px when focused.
 * This ensures inputs use at least 16px on mobile to prevent auto-zoom,
 * while preserving the original size on desktop (768px+).
 *
 * Uses Tailwind responsive classes for proper handling by twMerge.
 */
function getInputMobileFontClass(size: Size): string {
  // Only sm and md sizes need the override (they're < 16px)
  // lg and xl are already >= 16px
  // Use text-base (16px) on mobile, restore original size on md: breakpoint
  if (size === "sm") {
    return "!text-base md:!text-[length:var(--control-text-sm)]";
  }
  if (size === "md") {
    return "!text-base md:!text-[length:var(--control-text-md)]";
  }
  return "";
}

/**
 * Generates the complete set of base styles for input components.
 * This includes state styles, background, sizing, and custom classes.
 *
 * @example
 * ```tsx
 * const styles = getInputBaseStyles({
 *   isInvalid: formState.hasError,
 *   isDisabled: isLoading,
 *   size: "md"
 * });
 * ```
 */
export function getInputBaseStyles(props: InputStyleProps) {
  const { isInvalid, isDisabled, isFocused, transparent, size = "md", className } = props;

  return twMerge(
    "w-full",
    getInputStateStyles({ isInvalid, isDisabled, isFocused }),
    getInputBackgroundStyles({ transparent, isDisabled }),
    controlStyles[size].text,
    controlStyles[size].height,
    controlStyles[size].padding,
    getInputMobileFontClass(size),
    className
  );
}

/**
 * Generates styles for grouped form elements (like input with buttons)
 *
 * @example
 * ```tsx
 * <div className={getFieldGroupStyles({ size: "md" })}>
 *   <Input />
 *   <Button>Submit</Button>
 * </div>
 * ```
 */
export function getFieldGroupStyles(props: FieldGroupProps) {
  const { size = "md", isFocusWithin, isInvalid, isDisabled, transparent, className } = props;

  return twMerge(
    "group flex items-center rounded-[var(--control-border-radius)] overflow-hidden",
    getInputBackgroundStyles({ transparent, isDisabled }),
    controlStyles[size].height,
    getInputStateStyles({ isFocused: isFocusWithin, isInvalid, isDisabled }),
    className
  );
}

// Components

/**
 * Label component for form fields. Supports tooltips and required field indicators.
 *
 * @example
 * ```tsx
 * <Label
 *   htmlFor="email"
 *   tooltip="Enter your work email"
 *   isRequired
 * >
 *   Email Address
 * </Label>
 * ```
 */
export function Label({
  children,
  size = "md",
  tooltip,
  description,
  descriptionPlacement = "below",
  isRequired,
  className,
  htmlFor,
}: LabelProps) {
  const showTooltip = tooltip || (description && descriptionPlacement === "tooltip");
  const tooltipContent = tooltip || description;
  const showInlineDescription = description && descriptionPlacement === "inline";

  const labelContent = (
    <label
      htmlFor={htmlFor}
      className={twMerge(controlStyles[size].text, "!text-text-label font-semibold flex items-center gap-1", className)}
    >
      {children}
      {isRequired && <span>*</span>}
      {showTooltip && (
        <span className="inline-flex items-center text-text-muted">
          <Icon name="Info" size={size === "sm" ? 16 : 20} />
        </span>
      )}
      {showInlineDescription && <span className="text-text-caption font-normal ml-1">{description}</span>}
    </label>
  );

  if (showTooltip && tooltipContent) {
    return <Tooltip content={tooltipContent}>{labelContent}</Tooltip>;
  }

  return labelContent;
}

/**
 * Description component for providing additional context about a form field.
 *
 * @example
 * ```tsx
 * <Description>Must be at least 8 characters</Description>
 * ```
 */
export function Description({ size = "md", className, children, ...props }: DescriptionProps) {
  return (
    <div {...props} className={twMerge("leading-[2.25] text-text-caption", controlStyles[size].text, className)}>
      {children}
    </div>
  );
}

/**
 * Error message component for form fields. Automatically handles
 * accessibility attributes for screen readers.
 *
 * @example
 * ```tsx
 * {hasError && <FieldError>This field is required</FieldError>}
 * ```
 */
export function FieldError({ children, size = "md", className }: FieldErrorProps) {
  return (
    <div role="alert" className={twMerge(controlStyles[size].text, "text-feedback-error-text", className)}>
      {children}
    </div>
  );
}

/**
 * Base input component with consistent styling and state handling.
 * Extends the native input element with our custom styling system.
 *
 * @example
 * ```tsx
 * <Input
 *   size="md"
 *   isInvalid={hasError}
 *   isDisabled={isLoading}
 *   transparent={false}
 * />
 * ```
 */
export function Input({ size = "md", isInvalid, isDisabled, isFocused, transparent, className, ...props }: InputProps) {
  return (
    <input
      className={getInputBaseStyles({
        isInvalid,
        isDisabled,
        isFocused,
        transparent,
        size,
        className,
      })}
      {...props}
    />
  );
}

/**
 * Container for grouping form elements with consistent styling.
 * Useful for creating compound components like input with buttons.
 *
 * @example
 * ```tsx
 * <FieldGroup>
 *   <Input placeholder="Search..." />
 *   <Button>Search</Button>
 * </FieldGroup>
 * ```
 */
export function FieldGroup(props: FieldGroupProps) {
  const { children, ...rest } = props;
  return (
    <div {...rest} className={getFieldGroupStyles(props)}>
      {typeof children === "function" ? children(props) : children}
    </div>
  );
}
